import React, { useState, EventHandler } from 'react'
import { DropdownMenu } from '@paypalcorp/pp-react'
import { CodeBlock, CodeBlockWrapper } from '../components/layout'
import { Tab, Tabs } from './Tabs'

const initialValue = [
  { primaryText: 'HTML', value: 'html' },
  { primaryText: 'React', value: 'react' },
]

const PPCPIntegrationsDropdown = () => {
  const [selectedValue, setSelectedValue] = useState(initialValue[0].value)

  const handleOnChange: EventHandler<any> = (event: any) => {
    setSelectedValue(event.target.value)
  }

  return (
    <div data-testid="ppcp-integration-pattern-dropdown">
      <div style={{ marginTop: 10, marginBottom: 10, width: '250px' }}>
        <DropdownMenu
          id="ppcp-integration-pattern-dropdown"
          label="Select integration pattern"
          options={initialValue}
          value={selectedValue}
          onChange={handleOnChange}
        />
      </div>
      {selectedValue === 'html' && <HTMLIntegration />}
      {selectedValue === 'react' && <ReactIntegration />}
    </div>
  )
}

const ReactIntegration = () => {
  return (
    <div>
      <p>
        This integration includes a full-stack Node.js example. The{' '}
        <code>/client/checkout.jsx</code>, and <code>/server/server.js</code>{' '}
        file samples show how to render the PayPal buttons and the card field
        components in React.
      </p>
      <Tabs>
        {/* @ts-expect-error Tab is a dummy element. Label is accessed in Tabs  */}
        <Tab label="/client/checkout.jsx">
          <CodeBlockWrapper>
            <CodeBlock
              className="language-jsx"
              label="/client/checkout.ejs"
              children={`
import React, { useState } from "react";

import {
  PayPalScriptProvider,
  usePayPalCardFields,
  PayPalCardFieldsProvider,
  PayPalCardFieldsForm,
  PayPalButtons,
} from "@paypal/react-paypal-js";

export default function App() {
  const [isPaying, setIsPaying] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    adminArea1: "",
    adminArea2: "",
    countryCode: "",
    postalCode: "",
  });

  async function createOrder(data) {
    return fetch("myserver.com/api/orders", {
      method: "POST",
      // Use the "body" parameter to optionally pass additional order information
      body: JSON.stringify({
        cart: [
          {
            sku: "1blwyeo8",
            quantity: 2,
          },
        ],
        card: {
          attributes: {
            verification: {
              method: "SCA_ALWAYS",
            },
          },
        },
      }),
    })
      .then((response) => response.json())
      .then((order) => order.id)
      .catch((err) => {
        console.error(err);
      });
  }

  function onApprove(data) {
    return fetch(\`myserver.com/api/orders/\${data.orderID}/capture\`, {
      method: "POST",
    })
      .then((response) => response.json())
      .then((orderData) => {
        // Successful capture!
      })
      .catch((err) => {});
  }

  function onError(error) {
    // Do something with the error from the SDK
  }

  function handleBillingAddressChange(field, value) {
    setBillingAddress((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  return (
    <PayPalScriptProvider
      options={{
        clientId: "<%= clientId %>",
        components: "card-fields,buttons",
      }}
    >
      <PayPalButtons
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onError}
      />
      <PayPalCardFieldsProvider
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onError}
      >
        <PayPalCardFieldsForm />
        <input
          type="text"
          id="card-billing-address-line-2"
          name="card-billing-address-line-2"
          placeholder="Address line 1"
          onChange={(e) =>
            handleBillingAddressChange("addressLine1", e.target.value)
          }
        />
        <input
          type="text"
          id="card-billing-address-line-2"
          name="card-billing-address-line-2"
          placeholder="Address line 2"
          onChange={(e) =>
            handleBillingAddressChange("addressLine2", e.target.value)
          }
        />
        <input
          type="text"
          id="card-billing-address-admin-area-line-1"
          name="card-billing-address-admin-area-line-1"
          placeholder="Admin area line 1"
          onChange={(e) =>
            handleBillingAddressChange("adminArea1", e.target.value)
          }
        />
        <input
          type="text"
          id="card-billing-address-admin-area-line-2"
          name="card-billing-address-admin-area-line-2"
          placeholder="Admin area line 2"
          onChange={(e) =>
            handleBillingAddressChange("adminArea2", e.target.value)
          }
        />
        <input
          type="text"
          id="card-billing-address-country-code"
          name="card-billing-address-country-code"
          placeholder="Country code"
          onChange={(e) =>
            handleBillingAddressChange("countryCode", e.target.value)
          }
        />
        <input
          type="text"
          id="card-billing-address-postal-code"
          name="card-billing-address-postal-code"
          placeholder="Postal/zip code"
          onChange={(e) =>
            handleBillingAddressChange("postalCode", e.target.value)
          }
        />
        {/* Custom client component to handle card fields submission */}
        <SubmitPayment
          isPaying={isPaying}
          setIsPaying={setIsPaying}
          billingAddress={billingAddress}
        />
      </PayPalCardFieldsProvider>
    </PayPalScriptProvider>
  );
}

const SubmitPayment = ({ isPaying, setIsPaying, billingAddress }) => {
  const { cardFieldsForm, fields } = usePayPalCardFields();

  const handleClick = async () => {
    if (!cardFieldsForm) {
      const childErrorMessage =
        "Unable to find any child components in the <PayPalCardFieldsProvider />";

      throw new Error(childErrorMessage);
    }
    const formState = await cardFieldsForm.getState();

    if (!formState.isFormValid) {
      return alert("The payment form is invalid");
    }
    setIsPaying(true);

    cardFieldsForm.submit({ billingAddress }).catch((err) => {
      setIsPaying(false);
    });
  };

  return (
    <button
      className={isPaying ? "btn" : "btn btn-primary"}
      style={{ float: "right" }}
      onClick={handleClick}
    >
      {isPaying ? <div className="spinner tiny" /> : "Pay"}
    </button>
  );
};             
`}
            />
          </CodeBlockWrapper>
        </Tab>
        {/* @ts-expect-error Tab is a dummy element. Label is accessed in Tabs  */}
        <Tab label="/server/server.js">
          <CodeBlockWrapper>
            <CodeBlock
              className="language-javascript"
              label="/server/server1.js"
              children={`
app.get("/", async (req, res) => {
const clientId = process.env.PAYPAL_CLIENT_ID;
res.render("checkout", {
clientId,
});
});
`}
            />
          </CodeBlockWrapper>
        </Tab>
      </Tabs>
      <h3>Modify the code</h3>
      This section explains how to customize the PayPal buttons and card fields
      for your React integration.
      <h4>PayPal buttons</h4>
      <ol>
        <li>
          You should use styles that align with your brand using the{' '}
          <a href="/docs/checkout/advanced/customize/card-field-style/">
            supported CSS properties
          </a>{' '}
          inside the <code>style</code> prop.
        </li>
        <li>
          Optional: Customize configurations, such as <code>currency</code> and{' '}
          <code>intent</code> inside the <code>options</code> prop in the{' '}
          <code>PayPalScriptProvider</code>.
        </li>
      </ol>
      <h4>Card fields</h4>
      <ol>
        <li>
          Include the required card form elements: <code>card number</code>,{' '}
          <code>security code</code>, and <code>expiration date</code>. To learn
          about the available card form elements, see{' '}
          <a href="/sdk/js/reference/#link-cardfields">Card fields</a>. These
          are included out of the box in our <code>PayPalCardFieldsForm</code>{' '}
          component.
        </li>
        <li>
          Add more control over the rendered fields by using our{' '}
          <a href="https://paypal.github.io/react-paypal-js/?path=/docs/paypal-paypalcardfields-individual-fields--default">
            Individual Field components
          </a>
          .
        </li>
        <li>Add your own fields to accept billing address information.</li>
        <li>
          Optional: Change the outer styling of the card fields, such as{' '}
          <code>border</code>, <code>box-shadow</code>, and{' '}
          <code>background</code>. You can do this by adding a class or style to
          each individual field container.
        </li>
      </ol>
    </div>
  )
}

const HTMLIntegration = () => {
  return (
    <div>
      <p>
        This integration includes a full-stack Node.js example. The{' '}
        <code>/client/checkout.ejs</code>, <code>/public/app.js</code>, and{' '}
        <code>/server/server.js</code> file samples show how to render the
        PayPal buttons and the card fields component:
        <ul>
          <li>Use PayPal buttons to process PayPal payments.</li>
          <li>Use card fields to process card payments.</li>
        </ul>
        You'll need to:
        <ul>
          <li>
            Save the <code>checkout.ejs</code> file in a folder named{' '}
            <code>/client</code>.
          </li>
          <li>
            Save the <code>app.js</code> file in a folder named{' '}
            <code>/public</code>.
          </li>
          <li>
            Save the <code>server.js</code> file in a folder named{' '}
            <code>/server</code>.
          </li>
        </ul>
      </p>
      <Tabs>
        {/* @ts-expect-error Tab is a dummy element. Label is accessed in Tabs  */}
        <Tab label="/client/checkout.ejs">
          <CodeBlockWrapper>
            <CodeBlock
              className="language-html"
              label="/client/checkout.ejs"
              children={`
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<!-- To be replaced with your own stylesheet -->
<link
rel="stylesheet"
type="text/css"
href="https://www.paypalobjects.com/webstatic/en_US/developer/docs/css/cardfields.css"
/>
<!-- Express fills in the clientId variable -->
<script src="https://www.paypal.com/sdk/js?components=buttons,card-fields&client-id=<%= clientId %>"></script>
</head>
<body>
<div id="paypal-button-container" class="paypal-button-container"></div>
<div id="checkout-form">
<!-- Containers for Card Fields hosted by PayPal -->
<div id="card-name-field-container"></div>
<div id="card-number-field-container"></div>
<div id="card-expiry-field-container"></div>
<div id="card-cvv-field-container"></div>
<!-- To be replaced with your own Billing Address Fields -->
<div>
 <label for="card-billing-address-line-1">Billing Address</label>
 <input
   type="text"
   id="card-billing-address-line-1"
   name="card-billing-address-line-1"
   autocomplete="off"
   placeholder="Address line 1"
 >
</div>
<div>
 <input
   type="text"
   id="card-billing-address-line-2"
   name="card-billing-address-line-2"
   autocomplete="off"
   placeholder="Address line 2"
 >
</div>
<div>
 <input
   type="text"
   id="card-billing-address-admin-area-line-1""
   name="card-billing-address-admin-area-line-1""
   autocomplete="off"
   placeholder="Admin area line 1"
 >
</div>
<div>
 <input
   type="text"
   id="card-billing-address-admin-area-line-2"
   name="card-billing-address-admin-area-line-2"
   autocomplete="off"
   placeholder="Admin area line 2"
 >
</div>
<div>
 <input
   type="text"
   id="card-billing-address-country-code"
   name="card-billing-address-country-code"
   autocomplete="off"
   placeholder="Country code"
 >
</div>
<div>
 <input
   type="text"
   id="card-billing-address-postal-code"
   name="card-billing-address-postal-code"
   autocomplete="off"
   placeholder="Postal/zip code"
 >
</div>
<br><br>
<button id="card-field-submit-button" type="button">
 Pay now with Card Fields
</button>
</div>
<script src="./public/app.js"></script>
</body>
</html>
`}
            />
          </CodeBlockWrapper>
        </Tab>
        {/* @ts-expect-error Tab is a dummy element. Label is accessed in Tabs  */}
        <Tab label="/public/app.js">
          <CodeBlockWrapper>
            <CodeBlock
              className="language-javascript"
              label="/public/app1.js"
              children={`
// Render the button component
paypal
.Buttons({
// Sets up the transaction when a payment button is clicked
createOrder: function (data) {
return fetch("myserver.com/api/orders", {
 method: "POST",
 // Use the "body" parameter to optionally pass additional order information
 // such as product ID or amount
 body: {
   paymentSource: data.paymentSource,
 },
})
 .then((response) => response.json())
 .then((order) => order.id);
},
// Finalize the transaction after payer approval
onApprove: function (data) {
return fetch(\`myserver.com/api/orders/\${data.orderID}/capture\`, {
 method: "POST",
})
 .then((response) => response.json())
 .then((orderData) => {
   // Successful capture! For dev/demo purposes:
   console.log(
     "Capture result",
     orderData,
     JSON.stringify(orderData, null, 2),
   );
   var transaction = orderData.purchase_units[0].payments.captures[0];
   // Show a success message within this page. For example:
   // var element = document.getElementById('paypal-button-container');
   // element.innerHTML = '<h3>Thank you for your payment!</h3>';
   // Or go to another URL: actions.redirect('thank_you.html');
 });
},
onError: function (error) {
// Do something with the error from the SDK
},
})
.render("#paypal-button-container");\n
// Create the Card Fields Component and define callbacks
const cardField = paypal.CardFields({
createOrder: function (data) {
return fetch("myserver.com/api/orders", {
method: "POST",
body: {
 paymentSource: data.paymentSource,
},
})
.then((res) => {
 return res.json();
})
.then((orderData) => {
 return orderData.id;
});
},
onApprove: function (data) {
const { orderID } = data;
return fetch(\`myserver.com/api/orders/\${orderID}/capture\`, {
method: "POST",
})
.then((res) => {
 return res.json();
})
.then((orderData) => {
 // Redirect to success page
});
},
onError: function (error) {
// Do something with the error from the SDK
},
});
// Render each field after checking for eligibility
if (cardField.isEligible()) {
const nameField = cardField.NameField();
nameField.render("#card-name-field-container");\n
const numberField = cardField.NumberField();
numberField.render("#card-number-field-container");\n
const cvvField = cardField.CVVField();
cvvField.render("#card-cvv-field-container");\n
const expiryField = cardField.ExpiryField();
expiryField.render("#card-expiry-field-container");\n
// Add click listener to submit button and call the submit function on the CardField component
document
.getElementById("card-field-submit-button")
.addEventListener("click", () => {
cardField.submit({
 // From your billing address fields
 billingAddress: {
   addressLine1: document.getElementById(
     "card-billing-address-line-1",
   ).value,
   addressLine2: document.getElementById(
     "card-billing-address-line-2",
   ).value,
   adminArea1: document.getElementById(
     "card-billing-address-admin-area-line-1",
   ).value,
   adminArea2: document.getElementById(
     "card-billing-address-admin-area-line-2",
   ).value,
   countryCode: document.getElementById(
     "card-billing-address-country-code",
   ).value,
   postalCode: document.getElementById(
     "card-billing-address-postal-code",
   ).value,
 }
}).then(() => {
 // submit successful
});
});
}
`}
            />
          </CodeBlockWrapper>
        </Tab>
        {/* @ts-expect-error Tab is a dummy element. Label is accessed in Tabs  */}
        <Tab label="/server/server.js">
          <CodeBlockWrapper>
            <CodeBlock
              className="language-javascript"
              label="/server/server1.js"
              children={`
app.get("/", async (req, res) => {
const clientId = process.env.PAYPAL_CLIENT_ID;
res.render("checkout", {
clientId,
});
});
`}
            />
          </CodeBlockWrapper>
        </Tab>
      </Tabs>
      <h3>Modify the code</h3>
      This section explains how to customize the PayPal buttons and card fields
      for your integration.
      <h4>PayPal buttons</h4>
      <ol>
        <li>
          Copy a complete set of sample integration code from the{' '}
          <a href="https://github.com/paypal-examples/docs-examples/tree/main/advanced-integration/v2/">
            GitHub repo.
          </a>
        </li>
        <li>
          The{' '}
          <a href="https://www.paypalobjects.com/webstatic/en_US/developer/docs/css/cardfields.css">
            CSS file
          </a>{' '}
          in the <code>head</code> section is a sample for demo purposes.
          Instead, use styles that align with your brand using{' '}
          <a href="/docs/checkout/advanced/customize/card-field-style/">
            supported CSS properties
          </a>
          .
        </li>
        <li>
          Optional: Customize JavaScript configurations, such as{' '}
          <code>currency</code> and <code>intent</code>.
        </li>
        <li>
          Optional: Change the layout, width, height, and outer styling of the
          PayPal buttons, such as <code>border</code>, <code>box-shadow</code>,
          and <code>background</code>.
        </li>
      </ol>
      <h4>Card fields</h4>
      <ol>
        <li>
          Copy and paste both{' '}
          <a href="/docs/checkout/advanced/customize/card-field-style/#link-examples">
            examples of card field style objects
          </a>{' '}
          into your existing <code>/client/checkout.ejs</code> and{' '}
          <code>/public/app.js</code> files.
        </li>
        <li>
          Include the required card form elements: <code>card number</code>,{' '}
          <code>security code</code>, and <code>expiration date</code>. To learn
          about the available card form elements, see{' '}
          <a href="/sdk/js/reference/#link-cardfields">card fields</a>.
        </li>
        <li>Add your own fields to accept billing address information.</li>
        <li>
          A complete set of sample integration code is available from the{' '}
          <a href="https://github.com/paypal-examples/docs-examples/tree/main/advanced-integration/v2/">
            GitHub repo
          </a>
          .
        </li>
        <li>
          Optional: Change the layout, width, height, and outer styling of the
          card fields, such as <code>border</code>, <code>box-shadow</code>, and{' '}
          <code>background</code>. You can modify the elements you supply as
          containers.
        </li>
      </ol>
    </div>
  )
}

export default PPCPIntegrationsDropdown
